import "./index.css";
import { useEffect, useRef, useState } from "react";

function AshAndAmber() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let w = window.innerWidth;
    let h = window.innerHeight;
    const setSize = () => {
      const dpr = window.devicePixelRatio || 1;
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setSize();
    window.addEventListener("resize", setSize);

    const ember = document.createElement("canvas");
    ember.width = ember.height = 48;
    const ectx = ember.getContext("2d")!;
    const eg = ectx.createRadialGradient(24, 24, 0, 24, 24, 24);
    eg.addColorStop(0, "rgba(255, 205, 130, 1)");
    eg.addColorStop(0.25, "rgba(255, 130, 50, 0.7)");
    eg.addColorStop(0.6, "rgba(190, 60, 20, 0.28)");
    eg.addColorStop(1, "rgba(190, 60, 20, 0)");
    ectx.fillStyle = eg;
    ectx.fillRect(0, 0, 48, 48);

    type P = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
      life: number;
      max: number;
      kind: "ash" | "amber";
      phase: number;
    };
    const particles: P[] = [];

    const newAsh = (seeded = false): P => ({
      x: Math.random() * w,
      y: seeded ? Math.random() * h : -20,
      vx: (Math.random() - 0.5) * 0.3,
      vy: 0.15 + Math.random() * 0.45,
      r: 0.5 + Math.random() * 1.8,
      life: 0,
      max: 14000 + Math.random() * 10000,
      kind: "ash",
      phase: Math.random() * Math.PI * 2,
    });

    const newAmber = (seeded = false): P => ({
      x: Math.random() * w,
      y: seeded ? Math.random() * h : h + 20,
      vx: (Math.random() - 0.5) * 0.5,
      vy: -(0.25 + Math.random() * 0.75),
      r: 0.8 + Math.random() * 1.6,
      life: 0,
      max: 7000 + Math.random() * 7000,
      kind: "amber",
      phase: Math.random() * Math.PI * 2,
    });

    for (let i = 0; i < 90; i++) particles.push(newAsh(true));
    for (let i = 0; i < 45; i++) particles.push(newAmber(true));

    type Spark = { x: number; y: number; px: number; py: number; vx: number; vy: number; life: number; max: number };
    const sparks: Spark[] = [];
    type Flash = { x: number; y: number; life: number; max: number; size: number };
    const flashes: Flash[] = [];
    let nextStrike = performance.now() + 800 + Math.random() * 1800;

    const sectionTargets = Array.from(document.querySelectorAll<HTMLElement>("section"));
    const otherTargets: HTMLElement[] = [
      canvas,
      ...Array.from(document.querySelectorAll<HTMLElement>("nav")),
    ];
    let shakeUntil = 0;
    let shakeMag = 0;

    const strike = () => {
      const sx = w * (0.05 + Math.random() * 0.9);
      const sy = h - (12 + Math.random() * 36);
      const count = 28 + Math.floor(Math.random() * 22);
      for (let i = 0; i < count; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.95;
        const speed = 3.5 + Math.random() * 7.5;
        sparks.push({
          x: sx,
          y: sy,
          px: sx,
          py: sy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0,
          max: 500 + Math.random() * 550,
        });
      }
      flashes.push({ x: sx, y: sy, life: 0, max: 220, size: 70 + Math.random() * 40 });
      shakeUntil = performance.now() + 260;
      shakeMag = 7 + Math.random() * 6;
    };

    let raf = 0;
    let last = performance.now();
    const loop = (t: number) => {
      const dt = Math.min(40, t - last);
      last = t;

      ctx.clearRect(0, 0, w, h);

      const warmth = ctx.createRadialGradient(w / 2, h * 1.15, 0, w / 2, h * 1.15, h * 1.0);
      warmth.addColorStop(0, "rgba(200, 75, 30, 0.16)");
      warmth.addColorStop(1, "rgba(200, 75, 30, 0)");
      ctx.fillStyle = warmth;
      ctx.fillRect(0, 0, w, h);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i]!;
        p.x += p.vx * (dt / 16);
        p.y += p.vy * (dt / 16);
        p.x += Math.sin(p.life / 900 + p.phase) * 0.18;
        p.life += dt;

        const t01 = p.life / p.max;
        const fade = Math.sin(Math.PI * Math.min(1, t01));

        if (p.kind === "ash") {
          ctx.fillStyle = `rgba(220, 215, 205, ${0.22 * fade})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fill();
        } else {
          const size = p.r * 9;
          ctx.globalAlpha = fade * 0.9;
          ctx.drawImage(ember, p.x - size / 2, p.y - size / 2, size, size);
          ctx.globalAlpha = 1;
        }

        if (p.life > p.max || p.x < -40 || p.x > w + 40 || p.y < -40 || p.y > h + 40) {
          particles[i] = p.kind === "ash" ? newAsh() : newAmber();
        }
      }

      if (t > nextStrike) {
        strike();
        nextStrike = t + 2000 + Math.random() * 3500;
      }

      for (let i = flashes.length - 1; i >= 0; i--) {
        const f = flashes[i]!;
        f.life += dt;
        const t01 = f.life / f.max;
        if (t01 >= 1) {
          flashes.splice(i, 1);
          continue;
        }
        const alpha = (1 - t01) * 0.85;
        const radius = f.size * (0.4 + t01 * 0.9);
        const fg = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, radius);
        fg.addColorStop(0, `rgba(255, 215, 150, ${alpha})`);
        fg.addColorStop(0.4, `rgba(255, 120, 40, ${alpha * 0.55})`);
        fg.addColorStop(1, "rgba(220, 70, 20, 0)");
        ctx.fillStyle = fg;
        ctx.fillRect(f.x - radius, f.y - radius, radius * 2, radius * 2);
      }

      ctx.lineCap = "round";
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i]!;
        s.px = s.x;
        s.py = s.y;
        s.x += s.vx * (dt / 16);
        s.y += s.vy * (dt / 16);
        s.vy += 0.14 * (dt / 16);
        s.vx *= 0.985;
        s.life += dt;

        const t01 = s.life / s.max;
        if (t01 >= 1) {
          sparks.splice(i, 1);
          continue;
        }
        const alpha = 1 - t01;
        ctx.strokeStyle = `rgba(255, 195, 110, ${alpha})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(s.px, s.py);
        ctx.lineTo(s.x, s.y);
        ctx.stroke();

        ctx.fillStyle = `rgba(255, 235, 195, ${alpha})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }

      const isShaking = t < shakeUntil;
      let translatePart = "";
      if (isShaking) {
        const remaining = Math.max(0, (shakeUntil - t) / 260);
        const intensity = shakeMag * remaining;
        const dx = (Math.random() - 0.5) * intensity * 2;
        const dy = (Math.random() - 0.5) * intensity * 2;
        translatePart = `translate3d(${dx.toFixed(2)}px, ${dy.toFixed(2)}px, 0)`;
      }

      const scrollY = window.scrollY;
      for (let i = 0; i < sectionTargets.length; i++) {
        const sec = sectionTargets[i]!;
        let scale = 1;
        if (i < sectionTargets.length - 1) {
          const progress = Math.max(0, Math.min(1, (scrollY - i * h) / h));
          scale = 1 - progress * 0.3;
        }
        const scalePart = scale < 1 ? ` scale(${scale.toFixed(3)})` : "";
        const desired = translatePart + scalePart;
        if (sec.style.transform !== desired) sec.style.transform = desired;
      }

      for (const el of otherTargets) {
        if (el.style.transform !== translatePart) el.style.transform = translatePart;
      }

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", setSize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-[60] mix-blend-screen"
      aria-hidden
    />
  );
}

function Translation({ children }: { children: React.ReactNode }) {
  return (
    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-4 font-nav text-[10px] md:text-xs tracking-[0.4em] uppercase text-[#c44a1f] opacity-0 group-hover:opacity-100 transition-opacity duration-500 whitespace-nowrap">
      {children}
    </span>
  );
}

export function App() {
  const [showNav, setShowNav] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowNav(entry.intersectionRatio < 0.5),
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <AshAndAmber />
      <section
        id="home"
        ref={heroRef}
        className="sticky top-0 z-10 flex h-screen w-full items-center justify-center bg-black overflow-hidden"
      >
        <h1 className="gothic-glow relative inline-block group cursor-default font-gothic select-none text-[clamp(4rem,18vw,18rem)] leading-none text-[#f5f1e6]">
          <span>
            <span className="font-flourish text-[1.25em] align-middle mr-[-0.05em] text-[#c44a1f]">Z</span>
            eit
            <span className="font-flourish text-[1.25em] align-middle mx-[-0.03em] text-[#c44a1f]">F</span>
            orge
          </span>
        </h1>
      </section>

      <section
        id="about"
        className="sticky top-0 z-20 flex h-screen w-full items-center justify-center bg-black overflow-hidden border-t border-[#c44a1f]/20"
      >
        <div className="relative max-w-3xl px-8 text-center">
          <div className="font-flourish text-[#c44a1f] text-3xl tracking-[0.5em] mb-8">— II —</div>
          <h2 className="gothic-glow relative inline-block group cursor-default font-gothic text-[#f5f1e6] text-5xl md:text-7xl mb-16">
            <span>
              <span className="font-flourish text-[#c44a1f]">A</span>bout
            </span>
            <Translation>Who We Are</Translation>
          </h2>
          <p className="font-nav text-[#f5f1e6]/85 text-sm md:text-base leading-[2.2] tracking-[0.18em] uppercase">
            A team of absolute chads in software development &amp; engineering. Forged for performance and security above all else. Partnered with the finest designers on the planet — making things that stand the test of time.
          </p>
          <div className="mt-14 flex items-center justify-center gap-6 font-nav text-[10px] md:text-xs tracking-[0.5em] text-[#c44a1f]/80 uppercase">
            <span className="relative inline-block group cursor-default">
              <span>Tempus</span>
              <Translation>Time</Translation>
            </span>
            <span className="text-[#c44a1f]/40">·</span>
            <span className="relative inline-block group cursor-default">
              <span>Celeritas</span>
              <Translation>Speed</Translation>
            </span>
            <span className="text-[#c44a1f]/40">·</span>
            <span className="relative inline-block group cursor-default">
              <span>Securitas</span>
              <Translation>Security</Translation>
            </span>
          </div>
        </div>
      </section>

      <section
        id="work"
        className="sticky top-0 z-30 flex h-screen w-full items-center justify-center bg-black overflow-hidden border-t border-[#c44a1f]/20"
      >
        <div className="relative max-w-3xl px-8 text-center">
          <div className="font-flourish text-[#c44a1f] text-3xl tracking-[0.5em] mb-8">— III —</div>
          <h2 className="gothic-glow relative inline-block group cursor-default font-gothic text-[#f5f1e6] text-5xl md:text-7xl mb-16">
            <span>
              <span className="font-flourish text-[#c44a1f]">W</span>ork
            </span>
            <Translation>What We Forge</Translation>
          </h2>
          <p className="font-nav text-[#f5f1e6]/85 text-sm md:text-base leading-[2.2] tracking-[0.18em] uppercase">
            Selected forgings — under the hammer. Case studies and artifacts forthcoming.
          </p>
          <div className="mt-14 font-flourish text-[#c44a1f]/70 text-xl tracking-[0.4em] uppercase">
            <span className="relative inline-block group cursor-default">
              <span>Opera in Igne</span>
              <Translation>Works in the Fire</Translation>
            </span>
          </div>
        </div>
      </section>

      <section
        id="contact"
        className="sticky top-0 z-40 flex h-screen w-full items-center justify-center bg-black overflow-hidden border-t border-[#c44a1f]/20"
      >
        <div className="relative max-w-3xl px-8 text-center">
          <div className="font-flourish text-[#c44a1f] text-3xl tracking-[0.5em] mb-8">— IV —</div>
          <h2 className="gothic-glow relative inline-block group cursor-default font-gothic text-[#f5f1e6] text-5xl md:text-7xl mb-16">
            <span>
              <span className="font-flourish text-[#c44a1f]">C</span>ontact
            </span>
            <Translation>Send Word</Translation>
          </h2>
          <p className="font-nav text-[#f5f1e6]/70 text-xs md:text-sm leading-loose tracking-[0.25em] uppercase mb-12">
            Commissions, partnerships, and inquiries
          </p>
          <a
            href="mailto:info@zeitforge.com"
            className="gothic-glow relative inline-block group font-gothic text-[#f5f1e6] text-3xl md:text-5xl tracking-wide hover:text-[#c44a1f] transition-colors"
          >
            <span>
              info<span className="text-[#c44a1f]">@</span>zeitforge<span className="text-[#c44a1f]">.com</span>
            </span>
            <Translation>Reach the Forge</Translation>
          </a>
        </div>
      </section>

      <nav
        className={`fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center gap-12 bg-black/85 backdrop-blur py-6 text-[#f5f1e6] font-nav text-xs md:text-sm uppercase border-t border-[#c44a1f]/20 transition-opacity duration-700 ${
          showNav ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <a href="#home" className="hover:text-[#c44a1f] transition-colors">Home</a>
        <a href="#about" className="hover:text-[#c44a1f] transition-colors">About</a>
        <a href="#work" className="hover:text-[#c44a1f] transition-colors">Work</a>
        <a href="#contact" className="hover:text-[#c44a1f] transition-colors">Contact</a>
      </nav>
    </>
  );
}

export default App;
